import { FormEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Guid } from 'tsbase/System/Guid';
import { Result, Transaction } from '../../core/domain/module';
import { Auth, BrowserStorage, Subscriptions, Tracking } from '../../core/services/module';
import { SubscriptionProductFamilies } from '../../core/services/subscriptions/subscriptions';
import { LocalStorageKeys } from '../../core/services/browserStorage/keys/localStorageKeys';
import { Routes, QueryParams, ProductTiers, BreakingErrors, Messages, DismissibleErrors } from '../../core/enums/module';
import { CurrentStandardMonthlyProductIds } from '../../core/services/subscriptions/productIds';
import { DefaultSteps } from '../../configuration/default';
import { EmailCheckFields } from '../../configuration/steps/default/module';
import { getQueryParameter } from '../../utilities/getQueryParameter';
import { executeWithinBoundary } from '../../utilities/executeWithinBoundary';
import { getNextRoute } from '../../utilities/getNextRoute';
import { getEmailParam } from '../../utilities/getEmailParam';
import { getAccountMeta } from '../../utilities/getAccountMeta';
import { handleLoginResult } from '../../utilities/handleLoginResult';
import { passwordPostCheckoutIsEnabled } from '../../utilities/passwordPostCheckoutIsEnabled';
import { shouldUseCabelasBranding } from '../../utilities/shouldUseCabelasBranding';
import { TransactionField } from '../../components/TransactionFields/TransactionField';
import Layout from '../../components/Layout/Layout';
import { Loading } from '../../components/Loading/Loading';
import SubmitButton from '../../components/submitButton/SubmitButton';
import { Icons } from '../../icons';
import styles from './email.module.scss';
import { getVariantType } from '../../utilities/getVariantType';
import { VariantType } from '../../utilities/typeGuards/isVariantType';
import { FeatureFlagService } from '@delta-defense/feature-flag-service';
import { FeatureFlags } from '../../core/enums/featureFlags';
import { LoginActivateList } from '../../components/loginActivateList/LoginActivateList';

enum PurchaseType { Unknown, Membership, MagazineSubscription }

// eslint-disable-next-line complexity
export default function Email({
	transaction = Transaction.Instance(),
	TransactionFieldComponent = TransactionField,
	SubmitButtonComponent = SubmitButton,
	useRouterFunc = useRouter,
	getQueryParameterFunc = getQueryParameter,
	handleLoginResultFunc = handleLoginResult,
	localStorageService = BrowserStorage.LocalInstance(),
	auth = Auth.Instance(),
	tracking = Tracking.Instance(),
	standardProductsData,
	featureFlagLoaded = false,
	featureFlagService = FeatureFlagService.Instance()
}) {
	const router = useRouterFunc();
	Subscriptions.Instance().productFamiliesCache = standardProductsData || {};
	const emailCheckStep = transaction.Steps[DefaultSteps.EmailCheck];
	const [purchaseType, setPurchaseType] = useState(PurchaseType.Unknown);
	const [variantType, setVariantType] = useState(VariantType.Default);
	const [isABTestActive, setIsABTestActive] = useState(false);
	const productId = getQueryParameterFunc(QueryParams.ProductId);

	useEffect(() => {
		let emailCheckStepSub: string;
		transaction.SetCurrentStep(DefaultSteps.EmailCheck, false, true);

		executeWithinBoundary(async () => {
			emailCheckStepSub = await emailCheckStep.Submission.Subscribe(async (result) => {
				if (result?.value.accountExists) {
					router.push(`${Routes.SignupVerification}${location.search}`);
				} else if (!getEmailParam(location.search) && passwordPostCheckoutIsEnabled(router.asPath)) {
					transaction.Message.Publish(Messages.CreatingAccount);
					const email = transaction.Data.email;
					const leadDataToSend = await tracking.getTrackingFields();
					const password = Guid.NewGuid() + 'C';
					const createAccountResult = await auth.createAccount(email, password, leadDataToSend, getAccountMeta());

					transaction.Message.Publish(Messages.Empty);

					if (createAccountResult.wasSuccessful) {
						localStorageService.setItem(LocalStorageKeys.EmailThatPasswordWasGeneratedFor, email);
						await handleLoginResultFunc(Routes.Email, router)(createAccountResult);
					} else {
						transaction.Error.Publish(DismissibleErrors.ErrorCreatingAccount);
					}
				} else {
					router.push(`${getNextRoute(Routes.Email)}${location.search}`);
				}
			}, false);
		});

		return () => emailCheckStep.Submission.Cancel(emailCheckStepSub);
	}, []);

	useEffect(() => {
		setVariantType(getVariantType());

		executeWithinBoundary(async () => {
			const result = await transaction.SetProductDataById(productId);
			if (result.wasSuccessful) {
				const containsCcmProductData = result.value?.find((p) => p.attributes.tier === ProductTiers.CCM);
				setPurchaseType(containsCcmProductData ? PurchaseType.MagazineSubscription : PurchaseType.Membership);
			} else {
				transaction.Error.Publish(BreakingErrors.FailedToLoadProducts);
			}
		});
	}, []);

	useEffect(() => {
		if (featureFlagLoaded === true) {
			setIsABTestActive(featureFlagService.GetValue(FeatureFlags.ABTestSignupEmail) === 'true');
		}
	}, [featureFlagLoaded, featureFlagService]);

	const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		await emailCheckStep.Submit();
	};

	const useCabelasBranding = shouldUseCabelasBranding();

	const cabelasLoginText = (
		<p className={styles.lead}>
			Don't miss your shot at complete peace of mind, plus a FREE welcome gift. Enter your email to begin or reactivate
			your&nbsp;membership.
		</p>
	);
	const loginText = (
		<p className={styles.lead}>
			{' '}
			{!productId && (
				<>
					Membership starts at just ${variantType === VariantType.IIA ? 37 : 39}!<br />
				</>
			)}{' '}
			Enter your email to begin or reactivate your&nbsp;membership.
		</p>
	);
	const claimSubscriptionText = (
		<p>
			Claim your subscription to the nation's #1 resource for responsibly armed Americans.
			<br />
			Enter your email to get started.
		</p>
	);

	const getLoginText = () => {
		return isABTestActive ? <LoginActivateList /> : loginText;
	};

	const getMembershipH1 = () => {
		if (useCabelasBranding) {
			return <>Join Cabela's Shield Today</>;
		}
		return isABTestActive ? <>Activate Your USCCA <span>Benefits Today</span></> : <>Join the USCCA Today</>;
	};

	return (
		<Layout>
			{featureFlagLoaded === false && <Loading transaction={transaction} forcedMessage={Messages.LoadingSignupEmail} disableBackground />}
			<div className={`${styles.wrapper} ${variantType === VariantType.IIA ? styles.iia : ''}`}>
				<div className={`${styles.content} ${purchaseType === PurchaseType.MagazineSubscription ? styles.ccm : ''}`}>
					{featureFlagLoaded === true && (
						<>
							{purchaseType !== PurchaseType.Unknown && (
								<>
									<h1>
										{purchaseType === PurchaseType.Membership ? (
											<>{getMembershipH1()}</>
										) : (
											<>
												Subscribe to <i>Concealed Carry Magazine</i>
											</>
										)}
									</h1>
									{purchaseType === PurchaseType.Membership ? (useCabelasBranding ? cabelasLoginText : getLoginText()) : claimSubscriptionText}
								</>
							)}
							<form id="emailSetup" onSubmit={onSubmit}>
								<TransactionFieldComponent field={transaction.Steps[DefaultSteps.EmailCheck].Fields[EmailCheckFields.Email]} />
								<SubmitButtonComponent disableStickyBottomFeatureFlag>
									{getQueryParameterFunc(QueryParams.Email) ? 'Continue' : 'Get Started'} {Icons.ChevronUp}
								</SubmitButtonComponent>
							</form>
						</>
					)}
				</div>
			</div>
		</Layout>
	);
}

export async function getStaticProps() {
	const isStaging = process.env.DELTA_ENV === 'staging';
	if (isStaging) {
		process.env.X_Environment_Override = 'staging';
	}
	const productIds = isStaging
		? [
				CurrentStandardMonthlyProductIds.StagingGold,
				CurrentStandardMonthlyProductIds.StagingPlatinum,
				CurrentStandardMonthlyProductIds.StagingElite
			]
		: [CurrentStandardMonthlyProductIds.Gold, CurrentStandardMonthlyProductIds.Platinum, CurrentStandardMonthlyProductIds.Elite];
	Subscriptions.Destroy();
	const subscriptions = Subscriptions.Instance();

	const familiesResult = await subscriptions.getMembershipProducts(productIds[0]);
	const standardFamilies = familiesResult.value;
	const allStandardProductsReturned = !!standardFamilies && productIds.every((id) =>
		Object.values(standardFamilies).some((family) => family.monthly.id === id)
	);

	if (!familiesResult.wasSuccessful || !allStandardProductsReturned) {
		throw new Error('Failed to retrieve standard products - break build to avoid regression');
	}

	const standardProductsData: Record<string, Result<SubscriptionProductFamilies>> = {};
	productIds.forEach((id) => {
		standardProductsData[id] = familiesResult;
	});

	return {
		props: {
			standardProductsData
		}
	};
}
